import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import type { DropZoneProps } from './types';
import { isAllowUploadFile, isFileSizeValid } from './utils/validations';

type UseFileDropResult = {
  isDragging: boolean;
  file: File | null;
  isMaxFileSizeError: boolean;
  isAllowedExtensionError: boolean;
  reset: () => void;
  inputRefProps: {
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    multiple?: boolean;
  };
  dropZoneProps: DropZoneProps;
};

interface UseFileDropProps {
  setFiles?: (files: File[]) => void;
  files?: File[];
  allowedExtensions: string[];
  maxFileSize: number;
  maxFilesCount?: number;
  isMultipleFiles?: boolean;
}

export const useFileDrop = ({
  setFiles,
  files,
  allowedExtensions,
  maxFileSize,
  maxFilesCount = Number.MAX_SAFE_INTEGER,
  isMultipleFiles = false,
}: UseFileDropProps): UseFileDropResult => {
  const [file, setFile] = useState<File | null>(null);
  const [isMaxFileSizeError, setIsMaxFileSizeError] = useState(false);
  const [isAllowedExtensionError, setIsAllowedExtensionError] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
    setIsMaxFileSizeError(false);
    setIsAllowedExtensionError(false);
  }, []);

  const onDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const processFiles = useCallback(
    (inputFiles: File[]) => {
      const validFiles: File[] = [];
      let hasExtensionError = false;
      let hasSizeError = false;

      inputFiles.forEach((file) => {
        const isValidExtension = isAllowUploadFile(allowedExtensions, file);
        const isValidSize = isFileSizeValid(file, maxFileSize);

        if (!isValidExtension) {
          hasExtensionError = true;
        }
        if (!isValidSize) {
          hasSizeError = true;
        }
        if (isValidExtension && isValidSize) {
          validFiles.push(file);
        }
      });

      setIsAllowedExtensionError(hasExtensionError);
      setIsMaxFileSizeError(hasSizeError);
      if (validFiles.length > maxFilesCount) {
        toast.error(`You can only upload up to ${maxFilesCount} files`);
        return;
      }

      if (validFiles.length > 0) {
        if (!isMultipleFiles) {
          setFile(validFiles[0]);
          setFiles?.([validFiles[0]]);
        } else {
          setFiles?.([...(files ?? []), ...validFiles]);
        }
      }
    },
    [files, setFiles, allowedExtensions, isMultipleFiles, maxFileSize, maxFilesCount]
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const droppedFiles = Array.from(e.dataTransfer.files);
      processFiles(droppedFiles);
    },
    [processFiles]
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files?.length) return;
      if (e.target.files.length > maxFilesCount) {
        toast.error(`You can only upload up to ${maxFilesCount} files`);
        return;
      }
      const selectedFiles = Array.from(e.target.files);
      processFiles(selectedFiles);
      e.target.value = '';
    },
    [processFiles, maxFilesCount]
  );

  const reset = useCallback(() => {
    setFile(null);
    setIsMaxFileSizeError(false);
    setIsAllowedExtensionError(false);
    setIsDragging(false);
  }, []);

  return {
    isDragging,
    file,
    isMaxFileSizeError,
    isAllowedExtensionError,
    reset,
    inputRefProps: {
      onChange,
      multiple: isMultipleFiles,
    },
    dropZoneProps: {
      onDragOver,
      onDragLeave,
      onDrop,
    },
  };
};